import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Patient } from "common/models/types";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import { calculateAge } from "utils/TimeUtil";
import { formatGender } from "utils/gender";

interface PatientInfoDisplayProps {
  patient: Patient;
  onStartEdit?: () => void;
}

const InfoSection = styled.div`
  margin-bottom: 24px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const FullWidthInfoItem = styled(InfoItem)`
  grid-column: 1 / span 2;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  white-space: pre-line;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PatientInfoDisplay: React.FC<PatientInfoDisplayProps> = ({ patient, onStartEdit }) => {
  const age = calculateAge(patient.birthDate);

  return (
    <>
      <InfoSection>
        <SectionHeader>
          <Text textType="Subhead1">기본 정보</Text>
          {onStartEdit && (
            <Button
              buttonType="button"
              color="Primary"
              size="Small"
              type="Text"
              variant="Outlined"
              onClick={onStartEdit}
            >
              수정
            </Button>
          )}
        </SectionHeader>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>생년월일</InfoLabel>
            <InfoValue>
              {patient.birthDate ? new Date(patient.birthDate).toLocaleDateString() : "-"}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>나이</InfoLabel>
            <InfoValue>{age !== null ? `${age}세` : "-"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>성별</InfoLabel>
            <InfoValue>
              {formatGender(patient.gender)}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>임신 여부</InfoLabel>
            <InfoValue>{patient.pregnancyStatus ? "예" : "아니오"}</InfoValue>
          </InfoItem>
        </InfoGrid>
      </InfoSection>

      <InfoSection>
        <Text textType="Subhead1">의료 정보</Text>
        <InfoGrid>
          <FullWidthInfoItem>
            <InfoLabel>질병명</InfoLabel>
            <InfoValue>{patient.diseases || "-"}</InfoValue>
          </FullWidthInfoItem>
          <FullWidthInfoItem>
            <InfoLabel>알러지 정보</InfoLabel>
            <InfoValue>{patient.allergies || "-"}</InfoValue>
          </FullWidthInfoItem>
        </InfoGrid>
      </InfoSection>

      <InfoSection>
        <Text textType="Subhead1">상담 및 메모</Text>
        <InfoGrid>
          <FullWidthInfoItem>
            <InfoLabel>추가 정보</InfoLabel>
            <InfoValue>{patient.consultationNotes || "-"}</InfoValue>
          </FullWidthInfoItem>
          <FullWidthInfoItem>
            <InfoLabel>메모</InfoLabel>
            <InfoValue>{patient.memo || "-"}</InfoValue>
          </FullWidthInfoItem>
        </InfoGrid>
      </InfoSection>
    </>
  );
};

export default React.memo(PatientInfoDisplay);
